import { Link } from "@tanstack/react-router";
import { ShieldTick } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { useCookieConsentStore } from "@/stores/cookieConsentStore";

/**
 * Cookie consent banner displayed at the bottom of the page.
 * Shows until the user accepts or declines cookies.
 * Dismissing via the close button is treated as declining.
 */
export const CookieConsentBanner = () => {
  const status = useCookieConsentStore((state) => state.status);
  const acceptAll = useCookieConsentStore((state) => state.acceptAll);
  const rejectAll = useCookieConsentStore((state) => state.rejectAll);

  // Don't render if user has already made a choice
  if (status !== "pending") {
    return null;
  }

  const handleAccept = () => {
    acceptAll();
  };

  // Decline and dismiss are both treated as rejection
  const handleDecline = () => {
    rejectAll();
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      aria-describedby="cookie-consent-description"
      className="fixed inset-x-0 bottom-0 z-50 p-4"
    >
      <div className="mx-auto max-w-7xl">
        <div className="bg-secondary_subtle ring-primary relative flex flex-col gap-4 rounded-xl p-4 shadow-lg ring-1 ring-inset md:flex-row md:items-center md:gap-3 md:p-3">
          {/* Icon and text content */}
          <div className="flex flex-1 flex-col gap-4 md:w-0 md:flex-row md:items-center">
            <FeaturedIcon
              className="hidden md:flex"
              icon={ShieldTick}
              color="gray"
              theme="modern"
              size="lg"
            />

            <div
              id="cookie-consent-description"
              className="flex flex-col gap-0.5 overflow-hidden lg:flex-row lg:gap-1.5"
            >
              <p className="text-md text-secondary pr-8 font-semibold md:truncate md:pr-0">
                We use cookies to improve your experience
              </p>
              <p className="text-md text-tertiary md:truncate">
                Read our{" "}
                <Link
                  to="/privacy"
                  className="outline-focus-ring rounded-xs underline underline-offset-3 focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  Privacy Notice
                </Link>
                .
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <div className="flex w-full flex-col-reverse gap-3 md:flex-row">
              <Button
                color="secondary"
                size="lg"
                onClick={handleDecline}
                aria-label="Decline cookies"
              >
                Decline
              </Button>
              <Button color="primary" size="lg" onClick={handleAccept} aria-label="Accept cookies">
                Accept
              </Button>
            </div>

            {/* Close button - positioned absolutely on mobile, inline on desktop */}
            <div className="absolute top-2 right-2 flex shrink-0 items-center justify-center md:static">
              <CloseButton size="md" label="Dismiss cookie banner" onPress={handleDecline} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
